
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

// Database schema matches this interface
interface StaticContentDB {
  id: string;
  key: string;
  value: string;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface StaticContent {
  id: string;
  page: string;
  section: string;
  key: string;
  content: string;
  created_at: string | null;
  updated_at: string | null;
}

export function useStaticText() {
  const [staticContents, setStaticContents] = useState<StaticContent[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string>("");
  const [selectedPage, setSelectedPage] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  // Load static content from Supabase
  const loadStaticContent = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('static_content')
        .select('*')
        .order('key', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      // Map database schema to our interface
      const mappedData: StaticContent[] = (data || []).map((item: StaticContentDB) => ({
        id: item.id,
        // Parse page/section from key if needed (e.g., "home.hero" -> page: "home", section: "hero")
        page: item.key.split('.')[0] || 'general',
        section: item.key.split('.')[1] || 'content',
        key: item.key,
        content: item.value,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));
      
      setStaticContents(mappedData);
    } catch (error) {
      console.error("Error loading static content:", error);
      toast.error("Erro ao carregar textos fixos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStaticContent();
  }, []);

  // Get unique pages and sections
  const pages = [...new Set(staticContents.map(item => item.page))];
  const sections = selectedPage 
    ? [...new Set(staticContents.filter(item => item.page === selectedPage).map(item => item.section))]
    : [];
  
  // Filter content based on search term and selected page/section
  const filteredContents = staticContents.filter(item => {
    const matchesSearch = searchTerm === "" || 
                         item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPage = selectedPage === "" || item.page === selectedPage;
    const matchesSection = selectedSection === "" || item.section === selectedSection;
    
    return matchesSearch && matchesPage && matchesSection;
  });

  // Start editing a content
  const handleEdit = (content: StaticContent) => {
    setEditingId(content.id);
    setEditedContent(content.content);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedContent("");
  };

  // Save edited content
  const handleSave = async (id: string) => {
    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('static_content')
        .update({ 
          content: editedContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast.success("Conteúdo atualizado com sucesso");
      loadStaticContent();
      setEditingId(null);
      setEditedContent("");
    } catch (error) {
      console.error("Error updating content:", error);
      toast.error("Erro ao atualizar conteúdo");
    } finally {
      setIsSaving(false);
    }
  };

  return {
    staticContents,
    searchTerm,
    setSearchTerm,
    isLoading,
    editingId,
    editedContent,
    setEditedContent,
    selectedPage,
    setSelectedPage,
    selectedSection,
    setSelectedSection,
    isSaving,
    pages,
    sections,
    filteredContents,
    loadStaticContent,
    handleEdit,
    handleCancelEdit,
    handleSave
  };
}
